'use client';

import Link from 'next/link';
import { useState } from 'react';
import { Menu, X, ArrowRight, ChevronDown, Github } from 'lucide-react';
import Logo from './Logo';
import { platformLink } from '@/lib/config';
import { GITHUB_URL, NPM_PACKAGE_URL } from '@/lib/site';

const STATUS_URL = 'https://stats.uptimerobot.com/JVwO29f6ro';

interface NavItem {
  label: string;
  href: string;
  desc?: string;
  external?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Homepage anchors - visible as direct links, not hidden in dropdowns */
const DIRECT_LINKS: NavItem[] = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
];

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Product',
    items: [
      { label: 'Silker Shield', href: '/shield', desc: 'One script tag for any website' },
      { label: 'Mobile Security', href: '/mobile', desc: 'Blocking & pentest for iOS/Android' },
      { label: 'Web3 Tools', href: '/web3', desc: 'Free Solana security suite' },
      { label: 'Why Silker', href: '/why-us', desc: 'How we differ from a WAF' },
      { label: 'Architecture', href: '/architecture', desc: 'How it works under the hood' },
      { label: 'Benefits', href: '/benefits', desc: 'For builders, teams & founders' },
      { label: 'Roadmap', href: '/roadmap', desc: "What we've built & what's coming" },
    ],
  },
  {
    label: 'Developers',
    items: [
      { label: 'Documentation', href: '/docs', desc: 'Quickstart & integration guides' },
      { label: 'Quickstart', href: '/docs/quickstart', desc: 'Protect an app in minutes' },
      { label: 'Benchmarks', href: '/benchmarks', desc: 'Open detection results' },
      { label: 'Changelog', href: '/changelog', desc: 'Latest SDK releases' },
      { label: 'npm package', href: NPM_PACKAGE_URL, desc: '@silker-ai/agent', external: true },
      { label: 'GitHub', href: GITHUB_URL, desc: 'SDK source & issues', external: true },
    ],
  },
  {
    label: 'Resources',
    items: [
      { label: 'Best WAF', href: '/best-waf', desc: 'Buyer guide for modern apps' },
      { label: 'Reverse Proxy Guide', href: '/guides/what-is-a-reverse-proxy', desc: 'What a reverse proxy does' },
      { label: 'Cloudflare WAF Alternative', href: '/vs/cloudflare-waf', desc: 'Edge WAF vs runtime security' },
      { label: 'Case Studies', href: '/case-study', desc: 'Real-world AI breaches' },
      { label: 'FAQ', href: '/faq', desc: 'Common questions' },
      { label: 'Security', href: '/security', desc: 'How we protect your data' },
      { label: 'Status', href: STATUS_URL, desc: 'Live system uptime', external: true },
    ],
  },
  {
    label: 'Company',
    items: [
      { label: 'About', href: '/about', desc: 'Our mission & approach' },
      { label: 'Contact', href: '/contact', desc: 'Get in touch with us' },
    ],
  },
];

function itemProps(item: NavItem) {
  return item.external
    ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' as const }
    : { href: item.href };
}

const navLinkClass =
  'text-sm px-3 py-2 text-neutral-300 hover:text-white transition cursor-pointer';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const ctaHref = platformLink.register();

  return (
    <header className="sticky top-0 inset-x-0 z-[9999] bg-black border-b border-white/5 w-full">
      <div className="flex h-16 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-xl font-semibold text-white cursor-pointer"
        >
          <Logo className="w-8 h-8 rounded-lg" />
          <span className="bg-gradient-to-r from-white via-rose-200 to-white bg-clip-text text-transparent">
            Silker AI
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-x-0.5">
          {DIRECT_LINKS.map((link) => (
            <Link key={link.label} href={link.href} className={navLinkClass}>
              {link.label}
            </Link>
          ))}

          {NAV_GROUPS.map((group) => (
            <div
              key={group.label}
              className="relative"
              onMouseEnter={() => setOpenMenu(group.label)}
              onMouseLeave={() => setOpenMenu((m) => (m === group.label ? null : m))}
            >
              <button
                type="button"
                className={`inline-flex items-center gap-1 text-sm px-3 py-2 transition cursor-pointer ${
                  openMenu === group.label ? 'text-white' : 'text-neutral-300 hover:text-white'
                }`}
                aria-expanded={openMenu === group.label}
              >
                {group.label}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${openMenu === group.label ? 'rotate-180' : ''}`}
                />
              </button>

              {openMenu === group.label && (
                <div className="absolute left-0 top-full pt-2 w-64">
                  <div className="bg-neutral-950 border border-white/10 shadow-2xl shadow-black/50 p-1.5">
                    {group.items.map((item) => (
                      <Link
                        key={item.label}
                        {...itemProps(item)}
                        className="flex flex-col gap-0.5 px-3 py-2 hover:bg-white/5 border border-transparent hover:border-white/10 transition group/item cursor-pointer"
                        onClick={() => setOpenMenu(null)}
                      >
                        <span className="flex items-center gap-1.5 text-sm font-medium text-white">
                          {item.label}
                          {item.external && (
                            <ArrowRight className="w-3 h-3 -rotate-45 text-neutral-500 group-hover/item:text-rose-400 transition" />
                          )}
                        </span>
                        {item.desc && <span className="text-xs text-neutral-500">{item.desc}</span>}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={GITHUB_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="GitHub"
            className="hidden sm:inline-flex items-center justify-center w-9 h-9 text-neutral-300 hover:text-white border border-white/10 hover:border-white/30 transition cursor-pointer"
          >
            <Github className="w-[18px] h-[18px]" />
          </a>
          <a
            href={ctaHref}
            className="inline-flex transition overflow-hidden group text-sm font-medium rounded-none pt-2 pr-4 pb-2 pl-4 relative gap-x-2 items-center border border-white/20 hover:border-white/40 cursor-pointer"
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
              const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
              const text = e.currentTarget.querySelector('.text-gradient') as HTMLElement;
              if (arrow) {
                arrow.style.filter = 'drop-shadow(0 0 8px rgba(255,255,255,0.8))';
                arrow.style.transform = 'translateX(2px)';
              }
              if (text) text.style.backgroundPosition = '200% center';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
              const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
              const text = e.currentTarget.querySelector('.text-gradient') as HTMLElement;
              if (arrow) {
                arrow.style.filter = 'none';
                arrow.style.transform = 'translateX(0)';
              }
              if (text) text.style.backgroundPosition = '0% center';
            }}
          >
            <ArrowRight className="arrow-icon relative z-10 w-[18px] h-[18px] transition-all duration-400" />
            <span className="text-gradient relative z-10 bg-gradient-to-r from-white via-pink-200 to-white bg-[length:200%_100%] bg-[0%_center] transition-all duration-600 bg-clip-text text-transparent">
              Get started
            </span>
          </a>

          <button
            id="mobile-menu-toggle"
            type="button"
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-none text-neutral-300 hover:text-white hover:bg-white/5 transition cursor-pointer"
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-16 left-0 right-0 bg-black border-b border-white/10 shadow-2xl animate-slideDown max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="flex flex-col px-4 py-6 gap-5">
            <div className="flex flex-col gap-1">
              {DIRECT_LINKS.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-base text-neutral-300 hover:text-white transition py-2 px-3 hover:bg-white/5 cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {NAV_GROUPS.map((group) => (
              <div key={group.label} className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wider text-neutral-600 font-semibold px-3 mb-1">
                  {group.label}
                </span>
                {group.items.map((item) => (
                  <Link
                    key={item.label}
                    {...itemProps(item)}
                    className="flex items-center gap-1.5 text-base text-neutral-300 hover:text-white transition py-2 px-3 hover:bg-white/5 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                    {item.external && <ArrowRight className="w-3 h-3 -rotate-45 text-neutral-500" />}
                  </Link>
                ))}
              </div>
            ))}

            <div className="pt-2 border-t border-white/10">
              <a
                href={ctaHref}
                className="text-base text-rose-400 hover:text-rose-300 transition py-2 px-3 font-medium cursor-pointer"
              >
                Get started →
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
